import { Injectable, inject } from '@angular/core';
import { CmsApiService } from './api/cms-api.service';
import { catchError, first, map, tap } from 'rxjs/operators';
import { forkJoin, of, ReplaySubject } from 'rxjs';
import { TranslationService } from './translation/translation.service';
import { PlatformGraphQlService } from './graphql/platform-graphql.service';
import { symbolList } from '../helpers/currency-symbols';
import { PlatformService } from 'ngx-unificator/services';
import { WA_WINDOW } from 'ngx-unificator/tokens';

export const ASIAN_CURRENCIES = ['VND', 'IDR', 'MYR', 'THB', 'PHP', 'KRW'];

export const NOT_SS_CURRENCIES = [
  {
    isNotCurrency: true,
    code: 'TON',
    default: false,
    fiat: false,
    subcurrencies: [],
    subunits_to_unit: 0,
    symbol: 'TON',
  },
];

export const CountryList$: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
export const CurrencyList$: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
export const CryptoCurrencyList$: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
export const FiatCurrencyList$: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
export const GenderList$: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);

@Injectable({
  providedIn: 'root',
})
export class CommonDataService {

  private _platform = inject(PlatformService);
  private _api = inject(CmsApiService);
  private _platformApi = inject(PlatformGraphQlService);
  private _translate = inject(TranslationService);
  private _window = inject(WA_WINDOW);

  /**
   * List of available countries
   */
  private _countryList: Array<any> = [];

  /**
   * List of available currencies
   */
  private _currencyList: Array<any> = [];

  /**
   * List of available crypto currencies
   */
  private _cryptoCurrencyList: any[] = [];

  /**
   * List of available fiat currencies
   */
  private _fiatCurrencyList: any[] = [];

  /**
   * Emits when all data loaded
   */
  private _dataLoaded$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  /**
   * Access to _dataLoaded$ from outside
   */
  get loaded$(): ReplaySubject<boolean> {
    return this._dataLoaded$;
  }

  /**
   * Access to currency list from outside
   */
  get currencyList(): Array<any> {
    return this._currencyList;
  }

  /**
   * Access to crypto currency list from outside
   */
  get cryptoCurrencyList(): any[] {
    return this._cryptoCurrencyList;
  }

  /**
   * Access to fiat currency list from outside
   */
  get fiatCurrencyList(): any[] {
    return this._fiatCurrencyList;
  }

  /**
   * Access to country list from outside
   */
  get countryList(): Array<any> {
    return this._countryList;
  }

  /**
   * Returns symbol of provided currency code
   *
   * @param code
   */
  currencySymbol(code: string): string {
    return (this._currencyList.find(currency => currency.code === code) || {}).symbol || '';
  }

  /**
   * Returns name of country by country short
   *
   * @param short
   */
  countryName(short: string): string {
    return (this._countryList.find(country => country.short === short.toLowerCase()) || {}).name || '';
  }

  /**
   * Convert amount subunits to units (cents to euros)
   *
   * @param amount
   * @param currencyCode
   */
  subunitsToUnits(amount: number, currencyCode: string): number {
    return amount / ((this._currencyList.find(currency => currency.code === currencyCode) || {}).subunits_to_unit || 100);
  }

  /**
   * Convert amount units to subunits (euros to cents)
   *
   * @param amount
   * @param currencyCode
   */
  unitsToSubunits(amount: number, currencyCode: string): number {
    return amount * ((this._currencyList.find(currency => currency.code === currencyCode) || {}).subunits_to_unit || 100);
  }

  /**
   * Check if is crypto currency
   */
  isCryptoCurrency(currency: string): boolean {
    return this._cryptoCurrencyList.map(e => e.code).includes(currency);
  }

  /**
   * Get data from backend
   *
   * @public
   */
  public getData() {
    GenderList$.next([
      ['MALE', this._translate.translate('label.male')],
      ['FEMALE', this._translate.translate('label.female')],
    ]);

    forkJoin([
      this._api.countryList().pipe(
        catchError(error => of({ data: [] })),
        tap(response => {
          this._countryList = this._hideExtraCountriesForMainDomain(response.data || []);
          CountryList$.next(this._countryList);
        }),
      ),
      this._platformApi.currencyList().pipe(
        map(
          res =>
            res?.data?.result?.currencyList.map(e => {
              return {
                code: e,
                symbol: (symbolList.find(s => s.currency === e) || {})?.symbol || e,
                subunits_to_unit: 100,
                fiat: true,
              };
            }) || [],
        ),
        catchError(() => of([])),
        tap(currencyList => {
          this._currencyList = currencyList;
          this._cryptoCurrencyList = this._currencyList.filter(e => !e.fiat);
          this._fiatCurrencyList = this._currencyList.filter(e => e.fiat);
          CurrencyList$.next(this._currencyList);
          CryptoCurrencyList$.next(this._cryptoCurrencyList);
          FiatCurrencyList$.next(this._fiatCurrencyList);
        }),
        first(),
      ),
    ])
      .pipe(tap(() => this._dataLoaded$.next(true)))
      .subscribe();
  }

  private _hideExtraCountriesForMainDomain(list: any[]) {
    if (this._platform.isBrowser && this._window.location.hostname.includes('allstarcasino.com')) {
      const restrictCountryList = ['au', 'ie', 'ee', 'es', 'de', 'pl'];
      list = list.filter(e => !restrictCountryList.includes(e.short));
      return list;
    }
    return list;
  }

}
